import { Heart, Camera, Music, Clapperboard, Plane, Aperture } from "lucide-react";
import PageHero, { ScrollCta } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";

const IMG = {
  film: "https://images.unsplash.com/photo-1625690303837-654c9666d2d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  set: "https://images.unsplash.com/photo-1632187981988-40f3cbaeef5e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  aerialCity: "https://images.unsplash.com/photo-1499669404910-ba8b35824a3c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  aerialSite: "https://images.unsplash.com/photo-1532562145520-b8cce2486cd2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  interior:
    "https://static.prod-images.emergentagent.com/jobs/946b3673-7a0e-484a-808e-d45d9c8ae2d9/images/3e5af7d85ef50d0aa038a50eb8d8284172adb253bc7ff75ff237ae4cd7773085.jpeg",
  wedding: "https://images.unsplash.com/photo-1665960213508-48f07086d49c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  prewedding: "https://images.unsplash.com/photo-1735052712464-9d24b69be5f5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  couple: "https://images.unsplash.com/photo-1630526720753-aa4e71acf67d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  concert: "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  stage: "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
};

const SERVICES = [
  {
    icon: Heart,
    title: "Cinematic Weddings & Engagements",
    desc: "From intimate ring exchange ceremonies to grand wedding days, we capture candid emotions, traditional rituals, and breathtaking couple portraits. Our team blends into the background to document your genuine moments with a high-end, cinematic storytelling approach.",
    img: IMG.wedding,
  },
  {
    icon: Camera,
    title: "Pre-Wedding & Thematic Shoots",
    desc: "Bespoke, creatively directed photoshoots and short films set in stunning locations, designed to showcase your unique journey as a couple before the big day.",
    img: IMG.prewedding,
  },
  {
    icon: Music,
    title: "Live Concerts & Stage Shows",
    desc: "Dynamic, multi-camera coverage specifically engineered for live singer concerts, musical nights, and stage performances. We perfectly capture crisp artist close-ups, complex stage lighting aesthetics, and the raw energy of the crowd.",
    img: IMG.concert,
  },
  {
    icon: Clapperboard,
    title: "Corporate & Brand Films",
    desc: "High-impact promotional videos, product launches, and company profile documentaries designed to elevate your digital marketing campaigns and corporate identity.",
    img: IMG.film,
  },
  {
    icon: Plane,
    title: "Architectural & Drone Cinematography",
    desc: "Breathtaking aerial drone perspectives and wide-angle interior photography to showcase luxury real estate, commercial workspaces, and grand event venues.",
    img: IMG.aerialCity,
  },
];

const GALLERY = [
  { img: IMG.couple, label: "Wedding Day — Couple Portrait", span: "lg:col-span-2 lg:row-span-2" },
  { img: IMG.stage, label: "Live Concert — Stage Lighting" },
  { img: IMG.set, label: "On Set — Brand Film" },
  { img: IMG.aerialSite, label: "Aerial — Drone Perspective" },
  { img: IMG.interior, label: "Architectural Interior" },
];

const MEDIA_SERVICES = [
  "Cinematic Weddings & Engagements",
  "Pre-Wedding & Thematic Shoots",
  "Live Concerts & Stage Shows",
  "Corporate & Brand Films",
  "Architectural & Drone Cinematography",
];

export default function Media() {
  return (
    <div data-testid="media-page">
      <PageHero
        eyebrow="Weddings · Concerts · Brands · Spaces"
        title={["Videography &", "Photography."]}
        sub="Whether you are launching a global brand, hosting a live concert, or celebrating your biggest personal milestone, your visual story deserves to be captured flawlessly. At Evolve Elite, our high-end media production team delivers cinematic videography and striking photography that immortalizes the true essence of your event. We don't just shoot footage; we craft compelling, high-resolution visual narratives that leave a lasting legacy."
      >
        <ScrollCta href="#book-shoot" label="Book Your Shoot" testId="media-book-shoot-btn" />
      </PageHero>

      {/* SERVICES */}
      <section className="py-16 sm:py-24 lg:py-28" data-testid="media-services-section">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow">What We Shoot</p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Five genres. One cinematic standard.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06} className={i === 0 ? "sm:col-span-2 lg:col-span-2" : ""}>
                <div
                  className="card-lift group relative flex h-full min-h-[320px] flex-col justify-end overflow-hidden rounded-3xl bg-navy"
                  data-testid={`media-service-${i + 1}`}
                >
                  <img
                    src={s.img}
                    alt={s.title}
                    className="absolute inset-0 h-full w-full object-cover opacity-70 transition-transform duration-700 group-hover:scale-[1.05]"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/60 to-navy/10" />
                  <div className="relative p-7">
                    <s.icon className="h-8 w-8 text-gold" />
                    <h3 className="mt-5 font-display text-xl font-extrabold text-white sm:text-2xl">{s.title}</h3>
                    <p className="mt-3 max-w-md text-sm leading-relaxed text-white/75">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3} className="sm:col-span-2 lg:col-span-3">
              <div className="flex h-full flex-col gap-5 rounded-3xl border border-gold/40 bg-gold/10 p-7 sm:flex-row sm:items-center sm:gap-8">
                <Aperture className="h-10 w-10 shrink-0 text-amber-600" />
                <div>
                  <h3 className="font-display text-xl font-extrabold text-navy">Shot for every screen.</h3>
                  <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-700">
                    Every deliverable is cut for Reels, YouTube, LinkedIn and your website — colour-graded, captioned
                    and ready to publish the day it lands.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="border-y border-slate-200 bg-[#F8FAFC] py-16 sm:py-24" data-testid="media-gallery-section">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow">Showreel Stills</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              A frame from every kind of story.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:auto-rows-[260px]">
            {GALLERY.map((g, i) => (
              <Reveal key={g.label} delay={i * 0.06} className={g.span || ""}>
                <div className="group relative h-full min-h-[240px] overflow-hidden rounded-3xl" data-testid={`media-gallery-${i + 1}`}>
                  <img
                    src={g.img}
                    alt={g.label}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                    loading="lazy"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-navy/85 via-navy/30 to-transparent p-5 pt-14">
                    <p className="font-display text-lg font-extrabold text-white">{g.label}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BOOK A SHOOT */}
      <section id="book-shoot" className="grain relative bg-navy py-16 sm:py-24" data-testid="media-book-section">
        <div className="container-x relative z-10 grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow !text-gold">Production Enquiry</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Book Your Shoot.
            </h2>
            <p className="mt-5 max-w-md text-white/70">
              Wedding, concert, brand film or property — tell us what you're celebrating, launching or listing. We
              come back with a shot list, crew plan and a clear quote, usually within one working day.
            </p>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-7">
            <div className="rounded-3xl border border-white/10 bg-white p-6 shadow-2xl sm:p-8" data-testid="media-book-form-card">
              <LeadForm
                source="media-page"
                fields={["name", "phone", "email", "service", "message"]}
                services={MEDIA_SERVICES}
                cta="Book Your Shoot"
                idPrefix="media-book"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
